import devLog from './devLog';
/**
 * Session Manager - Centralized session and generation state management
 * Replaces scattered sessionStorage/localStorage writes throughout App.js
 */

const SESSION_KEYS = {
  SESSION_ID: 'ep_session_id',
  STREAMING_INPUT: 'ep_streaming_input',
  STREAMING_SESSION: 'ep_streaming_session',
  USE_STREAMING: 'ep_use_streaming',
  PROMPT_MODE: 'ep_prompt_mode',
  IS_UPGRADE: 'ep_is_upgrade',
  VERSION_TYPE: 'ep_version_type',
  COMPLETED_RESULT: 'ep_completed_result',
  CURRENT_RESULT: 'ep_current_result',
  LATEST_RESULT: 'ep_latest_result',
  NEW_PROJECT_DATA: 'ep_new_project_data',
  BUG_FIX_DATA: 'ep_bug_fix_data',
};

/**
 * Get or create a persistent session ID for the current browser session
 */
export const getSessionId = () => {
  let sessionId = sessionStorage.getItem(SESSION_KEYS.SESSION_ID);
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem(SESSION_KEYS.SESSION_ID, sessionId);
  }
  return sessionId;
};

/**
 * Prepare generation state before navigating to the streaming result page.
 * Purges all stale result data and sets fresh streaming parameters.
 * 
 * @param {Object} params
 * @param {string} params.input - User's raw input text
 * @param {string} params.mode - 'new_project' or 'bug_fixing'
 * @param {boolean} params.useStreaming - Whether to use streaming (default true)
 * @param {boolean} params.isUpgrade - Whether this is an upgrade request
 * @param {string} params.versionType - The version type for upgrades
 */
export const prepareGeneration = ({ input, mode, useStreaming = true, isUpgrade = false, versionType = '' }) => {
  const sessionId = getSessionId();

  // Purge ALL stale result data to prevent cross-generation contamination
  sessionStorage.removeItem(SESSION_KEYS.NEW_PROJECT_DATA);
  sessionStorage.removeItem(SESSION_KEYS.BUG_FIX_DATA);
  sessionStorage.removeItem(SESSION_KEYS.LATEST_RESULT);
  sessionStorage.removeItem(SESSION_KEYS.COMPLETED_RESULT);
  sessionStorage.removeItem(SESSION_KEYS.CURRENT_RESULT);

  // Set fresh streaming parameters
  sessionStorage.setItem(SESSION_KEYS.STREAMING_INPUT, input);
  sessionStorage.setItem(SESSION_KEYS.STREAMING_SESSION, sessionId);
  sessionStorage.setItem(SESSION_KEYS.USE_STREAMING, useStreaming ? 'true' : 'false');
  sessionStorage.setItem(SESSION_KEYS.PROMPT_MODE, mode);

  if (isUpgrade) {
    sessionStorage.setItem(SESSION_KEYS.IS_UPGRADE, 'true');
    sessionStorage.setItem(SESSION_KEYS.VERSION_TYPE, versionType || 'enhancement');
  }

  devLog.log(`[SessionManager] Generation prepared: mode=${mode}, streaming=${useStreaming}`);
  return sessionId;
};

export default {
  getSessionId,
  prepareGeneration,
  SESSION_KEYS,
};
